import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Zap, ArrowRight, Bot, Link2, BarChart3 } from "lucide-react";
import * as L from "lucide-react";
import { SiShopify, SiZapier } from "react-icons/si";
import LeadQualificationFlow from "./LeadQualificationFlow";
import { useContent } from "@/lib/cms";

// Branded integration logo chip — uses real brand colours + monogram for tools that don't have an SI icon
const Monogram = ({ name, bg, fg = "#fff", text }) => (
  <div
    className="h-10 w-10 rounded-xl flex items-center justify-center font-extrabold text-[15px] tracking-tight flex-shrink-0"
    style={{ backgroundColor: bg, color: fg, fontFamily: "Outfit, sans-serif" }}
    aria-hidden="true"
  >
    {text}
  </div>
);

const integrations = [
  { name: "Shopify", category: "E-commerce", href: "/integrations/shopify", icon: <SiShopify className="h-7 w-7" style={{ color: "#95BF47" }} /> },
  { name: "Zapier", category: "Automation", href: "/integrations/zapier", icon: <SiZapier className="h-7 w-7" style={{ color: "#FF4F00" }} /> },
  { name: "Pabbly Connect", category: "Automation", href: "/integrations/pabbly-connect", icon: <Monogram name="Pabbly" bg="#00857A" text="P" /> },
  { name: "Integrately", category: "Automation", href: "/integrations/integrately", icon: <Monogram name="Integrately" bg="#FFB100" fg="#1a1a1a" text="i" /> },
  { name: "Kylas CRM", category: "CRM", href: "/integrations/kylas-crm", icon: <Monogram name="Kylas" bg="#1463FF" text="K" /> },
  { name: "Magento by Meetanshi", category: "E-commerce", href: "/integrations/magento-meetanshi", icon: <Monogram name="Magento" bg="#EE672F" text="M" /> },
];

const FALLBACK = {
  eyebrow: "Visual flow builder",
  heading_pre: "Build WhatsApp",
  heading_highlight: "Automations Visually",
  description: "Build powerful WhatsApp automation workflows with an intuitive drag-and-drop Flow Builder. Automate customer conversations, qualify leads, connect your CRM and launch intelligent workflows—without writing a single line of code.",
  feature_list: [
    "Visual drag & drop builder",
    "AI intent detection (50+ languages)",
    "Conditional logic & smart branching",
    "CRM, APIs & webhook integrations",
    "Multi-agent routing & team assignment",
    "Deploy automations in minutes",
  ],
  capabilities: [
    { icon: "Bot",       title: "AI Powered",          sub: "98% accuracy",        tone: "violet" },
    { icon: "Zap",       title: "No-Code Builder",     sub: "Go live in minutes",  tone: "amber" },
    { icon: "Link2",     title: "CRM Ready",           sub: "100+ integrations",   tone: "blue" },
    { icon: "BarChart3", title: "Real-time Analytics", sub: "Track & optimise",    tone: "emerald" },
  ],
  trust_line: "Trusted by 900+ businesses to automate customer conversations on WhatsApp.",
};

const TONE_CLASSES = {
  violet:  "bg-violet-50 text-violet-600 ring-violet-100",
  amber:   "bg-amber-50 text-amber-600 ring-amber-100",
  blue:    "bg-blue-50 text-blue-600 ring-blue-100",
  emerald: "bg-emerald-50 text-emerald-600 ring-emerald-100",
  rose:    "bg-rose-50 text-rose-600 ring-rose-100",
  sky:     "bg-sky-50 text-sky-600 ring-sky-100",
};

const FALLBACK_CAP_ICON = { "AI Powered": Bot, "No-Code Builder": Zap, "CRM Ready": Link2, "Real-time Analytics": BarChart3 };

export const Chatbot = () => {
  const { data, enabled } = useContent("flow_builder", FALLBACK);
  if (!enabled) return null;
  const c = { ...FALLBACK, ...(data || {}) };

  return (
    <section className="py-20 sm:py-28 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">{c.eyebrow}</p>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            {c.heading_pre} <br />
            <span className="bg-gradient-to-r from-blue-600 to-emerald-500 bg-clip-text text-transparent">{c.heading_highlight}</span>
          </h2>
          <p className="mt-4 text-lg text-slate-600 max-w-xl">{c.description}</p>
          <ul className="mt-8 space-y-3">
            {(c.feature_list || []).map((t) => (
              <li key={t} className="flex items-start gap-3 text-slate-700">
                <CheckCircle2 className="h-5 w-5 mt-0.5 text-emerald-500 flex-shrink-0" />
                <span>{t}</span>
              </li>
            ))}
          </ul>

          {/* Capability pills — 4-up row, premium light cards */}
          <div className="mt-8 grid grid-cols-2 gap-3" data-testid="flow-capabilities">
            {(c.capabilities || []).map((cap) => {
              const Icon = L[cap.icon] || FALLBACK_CAP_ICON[cap.title] || Bot;
              return (
                <div
                  key={cap.title}
                  data-testid={`capability-${(cap.title || "").toLowerCase().replace(/\s+/g, "-")}`}
                  className="group rounded-2xl bg-white border border-slate-200/80 p-3.5 shadow-[0_1px_2px_rgba(2,6,23,0.04),0_8px_24px_rgba(2,6,23,0.05)] hover:-translate-y-0.5 hover:shadow-[0_4px_12px_rgba(2,6,23,0.06),0_18px_32px_rgba(2,6,23,0.08)] transition-all duration-300"
                >
                  <span className={`inline-flex h-9 w-9 rounded-xl items-center justify-center ring-1 ${TONE_CLASSES[cap.tone] || TONE_CLASSES.emerald}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="mt-3 text-sm font-bold text-slate-900 leading-tight">{cap.title}</div>
                  <div className="mt-1 text-xs text-slate-500">{cap.sub}</div>
                </div>
              );
            })}
          </div>

          <p className="mt-6 text-sm text-slate-500" data-testid="flow-trust-line">
            {c.trust_line}
          </p>
        </div>

        <div className="mt-10 lg:mt-0">
        <LeadQualificationFlow content={c} />
      </div>
      </div>

      {/* Integration showcase — below the workflow, same section */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.55 }}
        data-testid="chatbot-integrations"
        className="max-w-7xl mx-auto px-6 lg:px-8 mt-20"
      >
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <p className="text-sm uppercase tracking-[0.2em] font-bold text-emerald-600">Platform compatibility</p>
            <h3 className="mt-1.5 text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
              Connect Wappit to the tools you already use
            </h3>
          </div>
          <a
            href="#integrations"
            data-testid="chatbot-integrations-all"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-blue-700 hover:text-blue-800 group"
          >
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-100 px-2.5 py-1 text-xs text-emerald-700">
              <Zap className="h-3.5 w-3.5" /> 100+ Integrations Available
            </span>
            <span>See all</span>
            <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {integrations.map((ing) => (
            <a
              key={ing.name}
              href={ing.href}
              data-testid={`integration-badge-${ing.name.toLowerCase().replace(/\s+/g, "-")}`}
              aria-label={`Learn about Wappit's ${ing.name} integration`}
              className="group relative flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 overflow-hidden hover:shadow-premium-lg hover:-translate-y-1 hover:border-emerald-200 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
            >
              <span className="absolute inset-0 bg-gradient-to-br from-emerald-50/0 to-blue-50/0 group-hover:from-emerald-50/60 group-hover:to-blue-50/40 transition-colors pointer-events-none" aria-hidden="true" />
              <div className="relative flex items-center justify-center">{ing.icon}</div>
              <div className="relative min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-emerald-700 transition-colors">{ing.name}</div>
                <div className="text-[11px] text-slate-500">{ing.category}</div>
              </div>
              <ArrowRight className="relative ml-auto h-3.5 w-3.5 text-slate-300 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-emerald-600 transition-all" />
            </a>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
